import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  SmartToy,
  Assessment,
  Psychology,
  AutoMode,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import BotStatusIndicator from '../components/BotStatusIndicator';

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Empty data - will be populated from API
  const monthlyData: any[] = [];

  const channelData: any[] = [];

  const botPerformanceData: any[] = [];

  const botMetrics = {
    totalConversations: 0,
    averageConfidence: 0,
    successfulHandoffs: 0,
    averageResponseTime: 0,
    qualificationAccuracy: 0,
    customerSatisfaction: 0,
    activeLearningScenarios: 0,
    automationUptime: 0,
  };

  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      {/* KPI Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Total Leads</Typography>
            <Typography variant="h4" fontWeight="bold">0</Typography>
            <Typography variant="body2" sx={{ color: '#9E9E9E' }}>No data available</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Conversion Rate</Typography>
            <Typography variant="h4" fontWeight="bold">0%</Typography>
            <Typography variant="body2" sx={{ color: '#9E9E9E' }}>No data available</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>Avg Response Time</Typography>
            <Typography variant="h4" fontWeight="bold">0s</Typography>
            <Typography variant="body2" sx={{ color: '#9E9E9E' }}>No data available</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>AI Uptime</Typography>
            <Typography variant="h4" fontWeight="bold">0%</Typography>
            <Typography variant="body2" sx={{ color: '#9E9E9E' }}>No data available</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Trends */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3, height: 400 }}>
          <Typography variant="h6" gutterBottom>Monthly Performance Trends</Typography>
          <ResponsiveContainer width="100%" height="90%">
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Area dataKey="botAssisted" stackId="1" stroke="#2196F3" fill="#2196F3" name="AI Bot Assisted" />
              <Area dataKey="converted" stackId="1" stroke="#4CAF50" fill="#4CAF50" name="Converted" />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      {/* Channel Distribution */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, height: 400 }}>
          <Typography variant="h6" gutterBottom>Lead Channels</Typography>
          <ResponsiveContainer width="100%" height="90%">
            <PieChart>
              <Pie
                data={channelData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {channelData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderBotAnalyticsTab = () => (
    <Grid container spacing={3}>
      {/* AI Bot Status Card */}
      <Grid item xs={12} md={4}>
        <BotStatusIndicator
          status="active"
          confidence={89}
          variant="card"
          activeConversations={8}
          averageResponseTime={1.2}
          successRate={95.3}
        />
      </Grid>

      {/* Bot Performance Metrics */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3, height: 400 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">AI Performance Metrics</Typography>
            <Tooltip title="Refresh data">
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: '#2196F3' }}>{botMetrics.totalConversations}</Typography>
                <Typography variant="caption">Total Conversations</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: '#4CAF50' }}>{botMetrics.averageConfidence}%</Typography>
                <Typography variant="caption">Avg Confidence</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: '#FF9800' }}>{botMetrics.qualificationAccuracy}%</Typography>
                <Typography variant="caption">Qualification Accuracy</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: '#9C27B0' }}>{botMetrics.customerSatisfaction}/5</Typography>
                <Typography variant="caption">Customer Rating</Typography>
              </Box>
            </Grid>
          </Grid>
          <ResponsiveContainer width="100%" height="60%">
            <LineChart data={botPerformanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Line type="monotone" dataKey="confidence" stroke="#4CAF50" name="Confidence %" />
              <Line type="monotone" dataKey="accuracy" stroke="#2196F3" name="Accuracy %" />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      {/* Learning & Automation Metrics */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoMode sx={{ mr: 1, color: '#1a237e' }} />
            AI Learning & Automation Insights
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#E8F5E9' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                        Learning Performance
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        AI is continuously improving conversation quality
                      </Typography>
                    </Box>
                    <Psychology sx={{ fontSize: 40, color: '#4CAF50' }} />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Active Learning Scenarios: {botMetrics.activeLearningScenarios}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={85}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#C8E6C9' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      85% improvement this month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#E3F2FD' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#2196F3' }}>
                        Automation Success
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successful handoffs and lead qualification
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: '#2196F3' }} />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Successful Handoffs: {botMetrics.successfulHandoffs}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={botMetrics.successfulHandoffs}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#BBDEFB' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Exceeding industry standards
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* System Health Alerts */}
      <Grid item xs={12}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>System Status:</strong> All AI systems operational.
            Uptime: {botMetrics.automationUptime}% |
            Response Time: {botMetrics.averageResponseTime}s avg
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Analytics & AI Performance
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip
            icon={<SmartToy />}
            label="AI Bot Active"
            sx={{ bgcolor: '#E8F5E9', color: '#4CAF50' }}
          />
          <Button
            variant="outlined"
            startIcon={<Assessment />}
            sx={{ borderColor: '#1a237e', color: '#1a237e' }}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="AI Bot Analytics" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && renderOverviewTab()}
        {activeTab === 1 && renderBotAnalyticsTab()}
      </Box>
    </Box>
  );
};

export default Analytics;